import { confirmSignIn, rememberDevice } from "aws-amplify/auth";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { AuthError } from "@aws-amplify/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";

import {
  VerifyTotpFormData,
  verifyTotpSchema,
} from "../types/verify-totp.schema";

export function VerifyTotpForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const rememberMe =
    (location.state as { rememberMe?: boolean })?.rememberMe ?? false;
  const form = useForm<VerifyTotpFormData>({
    resolver: zodResolver(verifyTotpSchema),
    defaultValues: {
      code: "",
    },
  });

  const isSMSScreeen = location.pathname.includes("verify-sms");
  const isEmailScreen = location.pathname.includes("verify-email");
  async function onSubmit(values: VerifyTotpFormData) {
    try {
      await confirmSignIn({
        challengeResponse: values.code,
      });

      if (rememberMe) {
        await rememberDevice();
      }
    } catch (error) {
      if (error instanceof AuthError) {
        Sentry.captureException(error);

        switch (error.name) {
          case "CodeMismatchException":
            form.setError("code", {
              type: "manual",
              message:
                "The code you entered is incorrect or has expired. Please check your authenticator app and try again.",
            });
            break;
          case "VerifySoftwareTokenException":
            form.setError("code", {
              type: "manual",
              message: "Invalid verification code. Please try again.",
            });
            break;
          case "RespondToAuthChallengeException":
            form.setError("code", {
              type: "manual",
              message:
                "Invalid authentication challenge response. Please try again.",
            });
            break;
          case "AssociateSoftwareTokenException":
            form.setError("root", {
              type: "manual",
              message: "There was an error setting up MFA. Please try again.",
            });
            break;
          case "NotAuthorizedException":
            if (error.message.includes("Invalid session")) {
              navigate("/sign-in?mfaerror=true");
              break;
            }

            form.setError("root", {
              type: "manual",
              message: error.message,
            });
            break;
          case "SignInException":
            /**
             * This most likely occurred due to:
             * 1. signIn was not called before confirmSignIn.
             * 2. signIn threw an exception.
             * 3. page was refreshed during the sign in flow.
             */
            navigate("/sign-in?mfaerror=true");
            break;
          case "ExpiredCodeException":
            form.setError("root", {
              type: "manual",
              message: "Your software token has been already used once.",
            });
            break;
          default:
            form.setError("root", {
              type: "manual",
              message: "An unexpected error occurred. Please try again.",
            });
        }
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          type: "manual",
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  disabled={form.formState.isSubmitting}
                  onComplete={(code) => {
                    form.setValue("code", code);
                    form.handleSubmit(onSubmit)();
                  }}
                  autoFocus
                  autoComplete={
                    isSMSScreeen || isEmailScreen ? "off" : "one-time-code"
                  }
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}
        <Button
          type="submit"
          className="w-full"
          isLoading={
            form.formState.isSubmitting || form.formState.isSubmitSuccessful
          }
          isAnimated
        >
          Sign In
        </Button>
      </form>
    </Form>
  );
}
